import { useWorkspace } from './WorkspaceProvider';
import type { WorkspaceMembership } from '../api/types';
import type { AdvisorForFirm } from '../advisor/types';

/**
 * The `steward-advisor-firm` App Pack id (appcore docs/26 Phase 1). A firm
 * workspace mounts `['steward-app', 'steward-advisor-firm']`; an ordinary family
 * workspace mounts only `steward-app`. Kept private to this module — see
 * {@link workspaceKindFromPacks}'s header for why nothing else should grow its own
 * copy of this string or its own `packs.includes(...)` check.
 */
const FIRM_PACK_ID = 'steward-advisor-firm';

export type WorkspaceKind = 'family' | 'firm';

/**
 * Derives a workspace's kind from its mounted App Packs (appcore docs/26 Phase 0).
 * `'firm'` iff `steward-advisor-firm` is present; `'family'` for everything else,
 * including an absent, empty, or otherwise-unrecognized `packs` list. `'family'` is
 * the deliberate default: `handlers/me.ts` already degrades a failed workspace read
 * to `packs: []`, and the only kind that should ever require explicit evidence is
 * the less-common one (`'firm'`).
 *
 * ⚠️ **Keep the pack-id match in this ONE function.** This is the first concrete
 * consumer of crossmarc/appcore#246 (an `app`/`ui` descriptor on the pack manifest,
 * which will replace SPA-side id matching entirely). #246 should be able to land by
 * rewriting this function's body alone — so no other file should grow its own
 * `packs.includes(...)` or reference `FIRM_PACK_ID`-equivalent string literal.
 */
export function workspaceKindFromPacks(packs: string[] | undefined): WorkspaceKind {
  return packs?.includes(FIRM_PACK_ID) ? 'firm' : 'family';
}

/**
 * The active workspace's kind, derived from `useWorkspace().activeMembership.packs`
 * — see {@link workspaceKindFromPacks} for the derivation. UX gating only, the same
 * rule `useAdvisorFirm` states in its own header: this never authorizes anything,
 * it only decides what the SPA offers to show.
 *
 * **NOT sufficient on its own, and not merely "not yet" — this is a reachable production
 * state, not a rollout artifact still catching up.** A coordinator read-only investigation
 * across steward-app, crossmarc-ops and appcore, on 2026-09-08, traced the paths that create
 * a firm workspace and found none of them GUARANTEE the pack lands: crossmarc-ops's "Create
 * a firm" wizard submits `packs` from a free-text textarea merely *prefilled* with
 * `['steward-app','steward-advisor-firm']` — nothing client- or server-side asserts the firm
 * pack survives an edit to that field — and appcore's underlying
 * `POST /admin/tenants/{tenantId}/workspaces` takes `packs` as an optional caller-supplied
 * array that defaults to `[]` when omitted. Worse, the gap is **permanent, not transient**:
 * `steward-advisor-firm` is operator-only and appcore refuses to add it to an existing
 * workspace (`PACK_OPERATOR_ONLY_IMMUTABLE`), so a firm workspace provisioned without it
 * cannot be repaired from the console — only a credentialed seed-script run or a hand-written
 * table row fixes it. A separate same-day scan of `packs` on each environment's firm
 * workspace HEAD (dev, stage, prod) found all three currently mounting the pack — but that is
 * a reading of three rows as they happen to stand today, not a property of the paths that
 * create new ones, and it says nothing about a firm provisioned tomorrow through the same
 * wizard with the field edited.
 *
 * So `useWorkspaceKind()` answering `'family'` for a workspace that is really a firm's is a
 * state some real firm can be in, permanently, depending on how it was provisioned — neither
 * "every firm workspace mounts the pack" nor "no firm workspace ever mounts it without help"
 * is true, as a matter of what the CREATION PATHS allow. Closing that at the creation path is
 * appcore#733 — **merged to appcore's `main`** via appcore PR #734 (2026-09-23T21:53:26Z UTC,
 * verified via `gh pr view`): `writeFirmRecord` now refuses a firm record whose workspace
 * lacks the pack, so a firm record can no longer be CREATED pointing at a pack-less
 * workspace. That is a claim about appcore's `main` branch, not about what any environment is
 * running — this repo's own rule is that deploy state is verified separately, by hitting each
 * environment's `/version`, and no such check has been run here. Until each environment runs
 * an appcore build containing #734 (an appcore deploy fact, not a fact about this SPA's own
 * release), callers that decide something material on "is this the firm workspace" still
 * cannot trust this hook alone the moment membership is unknown — see {@link isFirmWorkspace}
 * below, which is what every such caller should call instead of re-deriving this question
 * itself. Separately, and permanently regardless of
 * appcore#733: this hook cannot answer AT ALL before `activeMembership` resolves, or when the
 * façade's own membership read degraded (`packsResolved: false`, `services/steward-app-api/
 * src/handlers/me.ts`) — {@link isFirmWorkspace}'s stand-in exists for exactly those two cases,
 * which are about the CLIENT's read of packs, not about whether appcore would ever let a
 * pack-less firm record exist.
 *
 * ⚠️ **This hook answering `'firm'` is NOT itself gated on `advisorFor`, and each caller
 * decides for itself whether ITS use of the answer should be** (steward-app#1271).
 * `Layout.tsx`'s pillar-suppression (via `useClientFrame`'s `inFirmWorkspace`) answers "is
 * this workspace a firm's", a fact about the workspace that is true or false independent of
 * whether this caller's own `GET /advisor/firms` has resolved yet. `Settings.tsx`'s
 * Advisors-tab suppression and `RequireFamilyWorkspace.tsx`'s route guard joined `Layout.tsx`
 * on that side in steward-app#1307, dropping a leading `!!advisorFor &&` that had made their
 * whole predicate `false` for a caller whose `GET /advisor/firms` had not resolved. Those
 * three, plus `HomeRoute.tsx`'s redirect (four call sites in total), now call
 * {@link isFirmWorkspace} below rather than each computing the question inline —
 * steward-app#736 (2026-09-23) extracted it for exactly that reason, after two of the four
 * had already drifted (the fallback described in #736's discussion as an unconditional `||`
 * arm reintroduced a genuine client-side loading-window race — `GET /advisor/firms` can
 * resolve before `GET /me`, so `activeWorkspaceId` is known while `activeMembership` is not —
 * that outright deletion would have reopened; the shared helper is what keeps the four in
 * lockstep going forward). `HomeRoute.tsx`'s REDIRECT to `/advisor`, by contrast, still wraps
 * the shared helper's answer in its own `placedAtSomeFirm &&` requiring `advisorFor` (or `firmChoice`)
 * truthy first, because redirecting a caller the server has not placed anywhere lands them on
 * a page that tells them the advisor area isn't for them — a claim about their ACCOUNT, not
 * about the workspace.
 *
 * **The discriminator is NOT "does it redirect".** `RequireFamilyWorkspace` redirects too
 * (to `/settings`) and still calls {@link isFirmWorkspace} bare, with no extra gate. It is
 * what the decision CLAIMS. A gate that answers "is this workspace a firm's" — suppress a
 * pillar, hide a tab, keep a page out of this workspace — calls the shared helper directly,
 * because that is a property of the workspace and nothing about the caller changes it. A gate
 * whose outcome asserts something about the CALLER'S ACCOUNT — `HomeRoute` sending them to
 * `/advisor`, which tells them they are an advisor — additionally requires that the façade
 * has placed the caller somewhere (`advisorFor`, or `firmChoice`), because without that the
 * SPA would be making the claim on its own. Read the callers' own comments before copying
 * either shape elsewhere; there is no one formula that covers both questions.
 */
export function useWorkspaceKind(): WorkspaceKind {
  const { activeMembership } = useWorkspace();
  return workspaceKindFromPacks(activeMembership?.packs);
}

/**
 * Is the given workspace a firm's — the predicate behind the four "hide this for a firm
 * workspace" / "redirect out of a firm workspace" gates that used to each hold their own copy
 * of the expression (steward-app#736, 2026-09-23): `HomeRoute.tsx`, `Settings.tsx`,
 * `useClientFrame.ts` and `RequireFamilyWorkspace.tsx`. **Not every `workspaceKind === 'firm'`
 * read in the SPA goes through this function** — `Access.tsx`'s Firm-staff checkbox,
 * `advisor/YourFirm.tsx`, `firstrun/welcome.ts`'s tour-suppression, and `HomeRoute.tsx`'s own
 * `holdForAdvisorQuery` line and bottom `FirmWorkspaceNotReady` branch all read the raw
 * `workspaceKind` directly, on purpose — none of them are exposed to the loading-window race
 * this function's stand-in covers, either because they answer a different question (a
 * checkbox's default, a query hold, a tour dismissal) or because `HomeRoute`'s own
 * `placedAtSomeFirm` gate already gives those two branches a narrower correctness argument, laid
 * out in their own comments. Read a call site's own docblock before assuming it goes through
 * here.
 *
 *     workspaceKind === 'firm' || (isMembershipUnresolved && !!advisorFor && activeWorkspaceId === advisorFor.firmWorkspaceId)
 *
 * where `isMembershipUnresolved` is `!activeMembership || activeMembership.packsResolved === false`.
 *
 * **Once `activeMembership` is known AND its packs read resolved, this is `workspaceKind ===
 * 'firm'` and NOTHING else** — the id-comparison second arm is unreachable in that case, by
 * construction (the early return below runs before it). That is deliberate, not an oversight:
 * steward-app#736's own investigation (2026-09-23, temporary local mutations at each exposed
 * call site, fully reverted) found that deleting the id-comparison arm outright was safe ONLY
 * once membership is loaded and its packs are known, because of two SEPARATE guarantees, not
 * one:
 *
 *   - **The steady state, once packs ARE known:** appcore#733, merged to appcore's `main` via
 *     PR #734 (2026-09-23T21:53:26Z UTC), makes `writeFirmRecord` refuse a firm record whose
 *     workspace lacks the pack — so a firm record can no longer be CREATED pointing at a
 *     pack-less workspace. That merge is not a deploy claim (this repo's rule: check each
 *     environment's `/version` separately); until this SPA's release reaches an environment
 *     running that appcore build, the guarantee for THAT environment still rests on the
 *     2026-09-23 ~20:22 UTC census in appcore#733's own body (dev 8/8, stage 1/1, prod 2/2
 *     firm records → pack-mounted workspaces) plus the two known creators mounting the pack
 *     before writing the record. A census is not an invariant and this function does not claim
 *     one.
 *   - **The stand-in, whenever packs are NOT known:** membership unresolved (`null`/`undefined`)
 *     or resolved with a degraded packs read (`packsResolved === false`, set by the façade's
 *     `me.ts` on a `getWorkspace` failure) both mean `workspaceKindFromPacks` has nothing
 *     trustworthy to derive `'firm'` from. Neither is about appcore's write-time guarantee at
 *     all — they are about what THIS client currently knows.
 *
 * The invariant this repo can pin in a test is narrower than either bullet on its own: for a
 * KNOWN membership with a RESOLVED packs read, packs decide alone (see this file's own
 * quadrant tests).
 *
 * **Two distinct reasons `workspaceKind` cannot answer, both covered by the same stand-in:**
 *
 *   1. `activeMembership` itself unknown — `null`, `undefined`, or still resolving because
 *      `/me` errored (`meLoaded` is `true` in that case too; do not key this on `meLoaded`).
 *      `useAdvisorFirm()`'s workspace-less `GET /advisor/firms` can resolve first (its own
 *      header: `enabled: !!token` rather than gated on a workspace), so `activeWorkspaceId` can
 *      already be known (the optimistic `localStorage` read in `WorkspaceProvider`) while
 *      membership is not — reproducing the #1271 flash shape one layer earlier.
 *   2. `activeMembership` known, but its `packs` read degraded — `packsResolved === false`
 *      (steward-app#736 F1, 2026-09-23, an independent review's finding verified at source:
 *      `services/steward-app-api/src/handlers/me.ts`'s catch branch keeps a membership alive on
 *      a `getWorkspace` failure with `packs: []`, and `[]` reads as an ordinary family
 *      workspace — indistinguishable from a real one — for that membership's `/me` `staleTime`,
 *      5 minutes). Before F1, a transient appcore error for a FIRM admin's own workspace would
 *      silently misread it as a family one for up to 5 minutes: Settings would offer
 *      Advisors/Connections/Workspace, `RequireFamilyWorkspace` would admit them, and
 *      `HomeRoute` would show the family Dashboard — the exact #798 wrong-target write path
 *      this predicate exists to keep closed.
 *
 * Both reasons route through `workspaceKindFromPacks(undefined)` reading `'family'`
 * (indistinguishable from a genuine family workspace) or through `packs: []` reading the same
 * way, which is why one boolean check (`isMembershipUnresolved` above) covers both: the
 * id-comparison arm is the stand-in for either kind of "not yet known", and ONLY that window —
 * it can never fire once membership is known with a resolved packs read, however that
 * resolves.
 *
 * In `RequireFamilyWorkspace.tsx` only the null/undefined half of the stand-in is ruled out
 * before this runs — that guard's own `if (!activeMembership) return null;` means
 * `activeMembership` is always truthy by the time it calls this helper, so the
 * `!activeMembership` half of `isMembershipUnresolved` can never be what fires there. The
 * `packsResolved === false` half is NOT ruled out and stays live at that site: a KNOWN
 * membership whose packs read degraded still reaches and triggers the id-comparison arm there
 * (F1, steward-app#736, 2026-09-23) — see that file's own docblock and
 * `RequireFamilyWorkspace.test.tsx`'s degraded-case test for why that is a correction, not dead
 * code left in. Calling the same shared helper from all four sites, rather than each reasoning
 * about which half applies to it, is what keeps them from drifting apart the way
 * steward-app#1307 found two of them had.
 *
 * No claim here about what is deployed anywhere; see `docs/26` / appcore#733 / appcore PR #734
 * for the write-time guarantee, and check each environment's `/version` separately for rollout.
 */
export function isFirmWorkspace(
  workspaceKind: WorkspaceKind,
  activeMembership: Pick<WorkspaceMembership, 'packsResolved'> | null | undefined,
  activeWorkspaceId: string | null | undefined,
  advisorFor: Pick<AdvisorForFirm, 'firmWorkspaceId'> | null | undefined,
): boolean {
  if (workspaceKind === 'firm') return true;
  const membershipUnresolved = !activeMembership || activeMembership.packsResolved === false;
  if (!membershipUnresolved) return false;
  return !!advisorFor && activeWorkspaceId === advisorFor.firmWorkspaceId;
}

/**
 * Is this workspace KNOWN to be a family's — steward-app#1742. People > Access (Settings >
 * Members until steward-app#1921) and its `/people/access` route must never show unless this
 * reads `true`; "unknown" hides it. This
 * is the opposite fail direction from {@link isFirmWorkspace} above, on purpose: that
 * predicate's callers fail closed toward treating an unresolved workspace AS a firm's, to keep a
 * #798-shaped wrong-target write closed; this one fails closed toward HIDING Members, because
 * Members and the Firm page's People section are the only two places the four firm-capability
 * checkboxes live, and showing both, or neither, in the same render is the failure to avoid.
 *
 * Defined simply as `!isFirmWorkspace(...)`, with one refinement: when the active membership's
 * packs read is DEGRADED (`packsResolved === false`), `isFirmWorkspace`'s own stand-in arm needs
 * `advisorFor` to answer at all, and `advisorFor` is only trustworthy once `useAdvisorFirm()`'s
 * `GET /advisor/firms` has actually SETTLED. `firmLookupUnsettled` (pass `isLoading || isError`
 * from that hook) is what tells this function the difference between "the lookup settled and
 * found no firm here" (shown) and "the lookup is still in flight, or failed, and there is
 * nothing yet to compare against" (hidden) — see the quadrant table below.
 *
 * A NON-degraded packs read never consults `firmLookupUnsettled` at all: `isFirmWorkspace`
 * answers from `workspaceKind` alone in that case (its own early-return), so a KNOWN firm
 * workspace hides Members immediately, with no wait on the firm lookup, and a KNOWN family
 * workspace shows it immediately too.
 *
 * Six reachable quadrants (steward-app#1742's brief), each pinned in
 * `useWorkspaceKind.test.ts`:
 *
 *   | membership          | packs      | lookup      | result   |
 *   |---------------------|------------|-------------|----------|
 *   | unknown (null)      | n/a        | n/a         | hidden   |
 *   | known               | firm       | n/a         | hidden   |
 *   | known               | family     | n/a         | shown    |
 *   | known, degraded     | n/a        | unsettled   | hidden   |
 *   | known, degraded     | n/a        | settled, no match  | shown  |
 *   | known, degraded     | n/a        | settled, matches   | hidden |
 *
 * Callers: `useAccessVisible.ts` (the People → Access tab) and `RedirectFirmAdminToFirmPage.tsx`
 * (the `/people/access` route) — both call this SAME function so a deep link cannot reach a page
 * the tab bar hides, or the reverse.
 */
export function isKnownFamilyWorkspace(
  workspaceKind: WorkspaceKind,
  activeMembership: Pick<WorkspaceMembership, 'packsResolved'> | null | undefined,
  activeWorkspaceId: string | null | undefined,
  advisorFor: Pick<AdvisorForFirm, 'firmWorkspaceId'> | null | undefined,
  firmLookupUnsettled: boolean,
): boolean {
  if (!activeMembership) return false;
  if (activeMembership.packsResolved === false && firmLookupUnsettled) return false;
  return !isFirmWorkspace(workspaceKind, activeMembership, activeWorkspaceId, advisorFor);
}
